import { Link } from "wouter";
import { Calculator, ChevronDown, ChevronUp } from "lucide-react";
import { countries } from "@shared/schema";
import { CookieSettingsButton } from "./CookieConsent";
import { useState } from "react";

export function Footer() {
  const currentYear = new Date().getFullYear();
  const [allCountries, setAllCountries] = useState(false);
  const moreCountries = countries.slice(5);
  const visibleCountries = allCountries ? moreCountries : moreCountries.slice(0, 7);

  return (
    <footer className="border-t border-border bg-card">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
          <div className="col-span-2 md:col-span-3 lg:col-span-1 space-y-4">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <Calculator className="h-5 w-5" />
              </div>
              <span className="text-lg font-bold">TaxYourSalary</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Calculate your take-home pay after tax in 45 countries. Free, accurate, and updated for 2026 tax years (UK/AU/NZ: 2026/2027).
            </p>
          </div>

          <div>
            <h3 className="font-semibold mb-4">Countries</h3>
            <ul className="space-y-2">
              {countries.slice(0, 5).map(country => (
                <li key={country.code}>
                  <Link 
                    href={`/calculator/${country.code}`}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    data-testid={`footer-link-${country.code}`}
                  >
                    {country.flag} {country.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4">More Countries</h3>
            <ul className="space-y-2">
              {visibleCountries.map(country => (
                <li key={country.code}>
                  <Link 
                    href={`/calculator/${country.code}`}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    data-testid={`footer-link-${country.code}`}
                  >
                    {country.flag} {country.name}
                  </Link>
                </li>
              ))}
            </ul>
            {moreCountries.length > 7 && (
              <button
                onClick={() => setAllCountries(v => !v)}
                className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                data-testid="footer-countries-toggle"
                aria-expanded={allCountries}
              >
                {allCountries ? (
                  <>Show less <ChevronUp className="h-3.5 w-3.5" /></>
                ) : (
                  <>Show all {moreCountries.length} countries <ChevronDown className="h-3.5 w-3.5" /></>
                )}
              </button>
            )}
          </div>

          <div>
            <h3 className="font-semibold mb-4">Tax Guides</h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  href="/blog/uk-salary-tax-calculator-2025"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-link-uk-tax-guide"
                >
                  UK Tax Guide
                </Link>
              </li>
              <li>
                <Link 
                  href="/blog/usa-federal-state-tax-guide-2025"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-link-usa-tax-guide"
                >
                  USA Tax Guide
                </Link>
              </li>
              <li>
                <Link 
                  href="/blog/germany-tax-system-explained"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-link-germany-tax-guide"
                >
                  Germany Tax Guide
                </Link>
              </li>
              <li>
                <Link 
                  href="/blog/ireland-usc-prsi-explained"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-link-ireland-tax-guide"
                >
                  Ireland Tax Guide
                </Link>
              </li>
              <li>
                <Link 
                  href="/blog/uk-60-percent-tax-trap"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-link-60-tax-trap"
                >
                  60% Tax Trap UK
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4">Resources</h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  href="/compare"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-link-compare"
                >
                  Compare Countries
                </Link>
              </li>
              <li>
                <Link 
                  href="/blog"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-link-blog"
                >
                  Tax Guides & Blog
                </Link>
              </li>
              <li>
                <Link 
                  href="/blog/tax-brackets-explained"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-link-tax-brackets"
                >
                  Tax Brackets Explained
                </Link>
              </li>
              <li>
                <Link 
                  href="/about"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-link-about"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link 
                  href="/contact"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-link-contact"
                >
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4">Legal</h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  href="/methodology"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-link-methodology"
                >
                  Methodology &amp; Sources
                </Link>
              </li>
              <li>
                <Link 
                  href="/privacy-policy"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-link-privacy"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link 
                  href="/terms-of-service"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-link-terms"
                >
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link 
                  href="/cookie-policy"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-link-cookies"
                >
                  Cookie Policy
                </Link>
              </li>
              <li>
                <Link 
                  href="/disclaimer"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-link-disclaimer"
                >
                  Disclaimer
                </Link>
              </li>
              <li>
                <Link 
                  href="/dmca"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-link-dmca"
                >
                  DMCA Policy
                </Link>
              </li>
              <li>
                <CookieSettingsButton />
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © {currentYear} TaxYourSalary. All rights reserved.
            </p>
            <p className="text-xs text-muted-foreground">
              Tax calculations are estimates only. Consult a tax professional for advice.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
